import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DayBucket } from '@busy/api';
import { DayHighlightsPopover } from './DayHighlightsPopover';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';

// Day-by-day revenue bars. Mirrors the demo's behavior 1:1:
//   - 7 bars visible at a time (bar width = wrap_width / 7 - gap)
//   - Horizontal scroll when range > 7 days
//   - Prev/next nav buttons advance by ~7 bars
//   - Fade gradients fade in on the overflow side(s)
//   - Auto-scrolls to today (right edge) on data change
//   - Today gets a subtle gold vertical highlight band behind the bar
//   - Bar tap opens DayHighlightsPopover with that day's stats + deltas
//
// Weather USED to live on the bars here (PAST weather chip per bar), but
// was moved to the Capacity tile's Week At A Glance heatmap — weather is
// a CAPACITY / PLANNING signal (rainy day = lighter walk-ins) and belongs
// alongside booking pacing, not retrospective revenue. The severe-weather
// legend swatch also moved off this chart along with the chips — there's
// no weather signal on these bars to legend anymore.
const BAR_GAP = 8;
const BARS_VISIBLE = 7;

export function RevenueChart({ days }: { days: DayBucket[] }) {
  const [selected, setSelected] = useState<DayBucket | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';
  const [barWidth, setBarWidth] = useState(60);
  const [overflow, setOverflow] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  // Bar scale: total (cuts + tips) per day. Tips render as a small
  // accent layered ON TOP of the cut portion of each bar, NOT stacked
  // above the existing chart — the chart's height (h-44 below) stays
  // fixed. Scaling against cuts+tips means the tallest bar still pegs
  // to the top of the container; tips just paint over the upper slice
  // of each bar in a different color.
  const max = useMemo(() => {
    let m = 0;
    for (const d of days) m = Math.max(m, d.revenue_cents + d.tip_cents);
    return m;
  }, [days]);

  // Peak day in the range — gets a small pulsing dot next to its label so the barber
  // can spot their best day at a glance. Mirrors the demo's bar-chart peak indicator.
  const peakMs = useMemo(() => {
    let best: number | null = null;
    let bestRev = 0;
    for (const d of days) {
      if (d.revenue_cents > bestRev) {
        bestRev = d.revenue_cents;
        best = d.ms;
      }
    }
    return best;
  }, [days]);

  const totals = useMemo(() => {
    const past = days.filter((d) => d.visits > 0);
    const denom = past.length || 1;
    const avgRevenue = past.reduce((s, d) => s + d.revenue_cents, 0) / denom;
    const avgVisits = past.reduce((s, d) => s + d.visits, 0) / denom;
    return { avgRevenue, avgVisits };
  }, [days]);

  // Today's calendar-day ms — used to highlight today's bar
  const todayMs = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t.getTime();
  }, []);

  // Compute bar width so exactly 7 bars span the wrap. Re-runs on resize.
  useLayoutEffect(() => {
    function recompute() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const w = wrap.clientWidth;
      if (w <= 0) return;
      // (BARS_VISIBLE bars + (BARS_VISIBLE-1) gaps) = w
      // → barWidth = (w - (BARS_VISIBLE-1)*GAP) / BARS_VISIBLE
      const next = Math.max(28, Math.floor((w - (BARS_VISIBLE - 1) * BAR_GAP) / BARS_VISIBLE));
      setBarWidth(next);
    }
    recompute();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recompute);
      return () => window.removeEventListener('resize', recompute);
    }
    const ro = new ResizeObserver(recompute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Auto-scroll to today (right edge) on mount + when the data changes.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollLeft = track.scrollWidth - track.clientWidth;
    updateOverflow(track);
  }, [days, barWidth]);

  // Update overflow state on scroll
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handler = () => updateOverflow(track);
    track.addEventListener('scroll', handler, { passive: true });
    return () => track.removeEventListener('scroll', handler);
  }, []);

  function updateOverflow(track: HTMLDivElement) {
    const left = track.scrollLeft;
    const right = track.scrollWidth - (track.scrollLeft + track.clientWidth);
    setOverflow({ left: left > 4, right: right > 4 });
  }

  function scrollByWeek(direction: -1 | 1) {
    const track = trackRef.current;
    if (!track) return;
    track.scrollLeft += (barWidth + BAR_GAP) * BARS_VISIBLE * direction;
    setTimeout(() => updateOverflow(track), 220);
  }

  if (max === 0 && days.every((d) => d.visits === 0)) {
    return (
      <div className="rounded-2xl border border-dashed border-line p-8 text-center text-ink-500">
        <p className="text-sm font-semibold">{t('dash.chart.empty.title')}</p>
        <p className="text-xs mt-1">{t('dash.chart.empty.body')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-line bg-surface p-4">
        <div ref={wrapRef} className="relative min-w-0">
          {/* Fade gradients */}
          <div
            className={`pointer-events-none absolute top-0 bottom-0 left-0 w-9 z-10 transition-opacity ${
              overflow.left ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ background: 'linear-gradient(to right, var(--surface, #fff), transparent)' }}
            aria-hidden
          />
          <div
            className={`pointer-events-none absolute top-0 bottom-0 right-0 w-9 z-10 transition-opacity ${
              overflow.right ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ background: 'linear-gradient(to left, var(--surface, #fff), transparent)' }}
            aria-hidden
          />

          {/* Prev/Next nav buttons */}
          {overflow.left && (
            <NavButton dir="prev" onClick={() => scrollByWeek(-1)} />
          )}
          {overflow.right && (
            <NavButton dir="next" onClick={() => scrollByWeek(1)} />
          )}

          {/* Scrollable track containing bars + day labels */}
          <div
            ref={trackRef}
            className="overflow-x-auto overflow-y-hidden scroll-smooth -mx-1 px-1"
            style={{ scrollbarWidth: 'none' }}
          >
            <div
              className="flex items-end gap-2 h-44"
              style={{ gap: `${BAR_GAP}px`, width: 'max-content' }}
            >
              {days.map((d) => {
                const dayTotal = d.revenue_cents + d.tip_cents;
                // Bar = cuts + tips total. Tips paint as the BASE slice
                // (bottom) of the bar in a different color — tips are
                // typically smaller than cut revenue, so anchoring them
                // at the base reads more naturally (small foundation
                // under the larger cuts portion). The container height
                // is fixed; the tip color just paints over the lower
                // portion of each bar.
                const totalHeightPct = max > 0 ? (dayTotal / max) * 100 : 0;
                const tipsSharePct =
                  dayTotal > 0 ? (d.tip_cents / dayTotal) * 100 : 0;
                const isToday = d.ms === todayMs;
                const empty = dayTotal === 0;
                return (
                  <button
                    key={d.ms}
                    type="button"
                    onClick={() => setSelected(d)}
                    aria-label={t('dash.chart.barAria', {
                      date: new Date(d.ms).toLocaleDateString(dateLocale),
                      rev: (d.revenue_cents / 100).toFixed(0),
                    })}
                    className="relative shrink-0 flex flex-col items-center justify-end h-full group cursor-pointer"
                    style={{ width: `${barWidth}px` }}
                  >
                    {isToday && (
                      <span
                        aria-hidden
                        className="absolute inset-0 rounded-md bg-gold-500/10 -mx-1"
                      />
                    )}
                    {empty ? (
                      <span className="relative w-full h-1 rounded-t bg-bg-alt border-t border-line" />
                    ) : (
                      // Stacked bar — relative parent holds the total
                      // height; the tip slice absolutely-positions at
                      // the BASE so the chart container height is fixed
                      // and tips form a small foundation under the
                      // (typically taller) cut-revenue portion.
                      <span
                        className={`relative w-full rounded-t transition-all group-hover:opacity-80 ${
                          isToday
                            ? 'bg-gradient-to-t from-gold-500 to-gold-300'
                            : 'bg-navy-900 dark:bg-gold-500'
                        }`}
                        style={{ height: `${totalHeightPct}%`, minHeight: 2 }}
                      >
                        {d.tip_cents > 0 && (
                          <span
                            aria-hidden
                            // `bottom-0` anchors the tip slice to the
                            // base of the bar. No corner rounding —
                            // the outer bar wrapper rounds only the
                            // TOP, so a flat-edged base matches the
                            // bar's natural square bottom.
                            className="absolute bottom-0 left-0 right-0 bg-green-500 dark:bg-green-400"
                            style={{ height: `${tipsSharePct}%` }}
                          />
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Day labels share the same scroll container so they always align with bars */}
            <div className="flex mt-2 pb-1" style={{ gap: `${BAR_GAP}px`, width: 'max-content' }}>
              {days.map((d) => {
                const isToday = d.ms === todayMs;
                const isPeak = d.ms === peakMs && d.revenue_cents > 0;
                const date = new Date(d.ms);
                return (
                  <div
                    key={`label-${d.ms}`}
                    className="shrink-0 text-center"
                    style={{ width: `${barWidth}px` }}
                  >
                    <div
                      className={`text-[10px] font-bold tabular-nums inline-flex items-center justify-center gap-1 ${
                        isToday ? 'text-navy-900 dark:text-gold-500' : 'text-ink-700'
                      }`}
                    >
                      {date.toLocaleDateString(dateLocale, { weekday: 'short' }).slice(0, 3)}
                      {isPeak && (
                        // Gold star — distinct from the green tip
                        // slice on bars (the other in-chart accent
                        // color). Star shape + gold fill makes the
                        // peak indicator unmistakable.
                        <span
                          className="inline-flex"
                          title={t('dash.chart.peakTitle')}
                          aria-label={t('dash.chart.peakTitle')}
                        >
                          <PeakStarIcon className="w-2.5 h-2.5 text-gold-500 dark:text-gold-400" />
                        </span>
                      )}
                    </div>
                    <div className="text-[9px] text-ink-500 tabular-nums">
                      {date.toLocaleDateString(dateLocale, { month: 'short' })} {date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-[10px] tracking-[0.12em] uppercase font-bold text-ink-500 gap-3 flex-wrap">
          <span>{t('dash.chart.tapHint')}</span>
          <span className="inline-flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-navy-900 dark:bg-gold-500" />
              {t('dash.chart.legend.cuts')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-green-500 dark:bg-green-400" />
              {t('dash.chart.legend.tips')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <PeakStarIcon className="w-3 h-3 text-gold-500 dark:text-gold-400" />
              {t('dash.chart.legend.peak')}
            </span>
          </span>
        </div>
      </div>

      {selected && (
        <DayHighlightsPopover
          day={selected}
          rangeAvgRevenueCents={totals.avgRevenue}
          rangeAvgVisits={totals.avgVisits}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function NavButton({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'prev' ? t('dash.chart.prev') : t('dash.chart.next')}
      className={`absolute top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-surface border border-line shadow-md grid place-items-center text-ink-700 hover:text-navy-900 dark:hover:text-gold-500 transition-colors ${
        dir === 'prev' ? '-left-2' : '-right-2'
      }`}
    >
      <svg
        className="w-3.5 h-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {dir === 'prev' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 6 15 12 9 18" />
        )}
      </svg>
    </button>
  );
}

/** Five-point gold star — peak-day indicator. Chosen to be unambiguous
 *  against the green tip slice on bars (the other in-chart accent
 *  color). Weather chips + severe-weather indicator live on the
 *  Capacity tile now, not here. */
function PeakStarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden
    >
      <path d="M12 2.5l2.92 5.92 6.53.95-4.72 4.6 1.11 6.5L12 17.4l-5.84 3.07 1.11-6.5L2.55 9.37l6.53-.95L12 2.5z" />
    </svg>
  );
}
